import { useEffect, useMemo, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { listWijken } from '../../../helpers/pdok/fetchPdok'

// Step 2 — multiselect wijken of the chosen cities (keyless PDOK).
// Persists to Wervingsacties.Wijken[] via props.
const WijkSelect = ({ gemeenten, selectedWijken, onToggle, canEdit }) => {
    const [options, setOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [query, setQuery] = useState('')

    // Stable dependency so the fetch effect doesn't re-run on every snapshot
    // (campaign arrays get a fresh reference on each Firestore update).
    const gemeenteKey = useMemo(() => gemeenten.map((g) => g.Naam).sort().join('|'), [gemeenten])

    useEffect(() => {
        if (gemeenten.length === 0) {
            setOptions([])
            return
        }
        const controller = new AbortController()
        let cancelled = false
        setLoading(true)
        Promise.all(gemeenten.map((g) => listWijken(g.Naam, controller.signal)))
            .then((lists) => { if (!cancelled) setOptions(lists.flat()) })
            .catch((err) => { if (err.name !== 'AbortError') console.error('PDOK wijk list failed', err) })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => {
            cancelled = true
            controller.abort()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gemeenteKey])

    const selectedCodes = useMemo(() => new Set(selectedWijken.map((w) => w.WijkCode)), [selectedWijken])

    // Already-selected wijken, shown on top. They carry their own name + city,
    // so they render even before the PDOK options finish loading.
    const selectedList = useMemo(
        () => [...selectedWijken].sort((a, b) =>
            (a.GemeenteNaam || '').localeCompare(b.GemeenteNaam || '') ||
            (a.Naam || '').localeCompare(b.Naam || '')
        ),
        [selectedWijken]
    )

    // Available list = loaded options minus the selected ones, then search.
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        return options.filter((w) => {
            if (selectedCodes.has(w.WijkCode)) return false
            if (!q) return true
            return `${w.Naam} ${w.GemeenteNaam}`.toLowerCase().includes(q)
        })
    }, [options, query, selectedCodes])

    if (gemeenten.length === 0) return null

    return (
        <div className='wa-step'>
            <h3>2. Wijken <span className='wa-sub'>{selectedWijken.length} geselecteerd</span></h3>
            {loading && (
                <p className='wa-hint wa-loading'>
                    <svg className='wa-spinner' viewBox='0 0 24 24' width='16' height='16' aria-hidden='true'>
                        <circle cx='12' cy='12' r='9' fill='none' stroke='#e6e6e6' strokeWidth='3' />
                        <path d='M12 3 a9 9 0 0 1 9 9' fill='none' stroke='#47acc3' strokeWidth='3' strokeLinecap='round' />
                    </svg>
                    Wijken laden
                </p>
            )}
            {selectedList.length > 0 && (
                <div className='wa-list wa-selected'>
                    {selectedList.map((w) => (
                        <div
                            key={w.WijkCode}
                            className='wa-option wa-option-selected'
                            onClick={canEdit ? () => onToggle(w) : undefined}
                        >
                            <span>{w.Naam} <span className='wa-sub'>{w.GemeenteNaam}</span></span>
                            {canEdit && <CloseIcon className='wa-option-remove' />}
                        </div>
                    ))}
                </div>
            )}
            {options.length > 0 && (
                <div className='wa-search'>
                    <input
                        type='text'
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder='Zoek een wijk'
                    />
                </div>
            )}
            <div className='wa-list'>
                {filtered.map((w) => (
                    <div
                        key={w.WijkCode}
                        className='wa-option'
                        onClick={canEdit ? () => onToggle(w) : undefined}
                    >
                        {w.Naam} <span className='wa-sub'>{w.GemeenteNaam}</span>
                    </div>
                ))}
                {!loading && options.length === 0 && <p className='wa-empty'>Geen wijken gevonden.</p>}
                {!loading && options.length > 0 && filtered.length === 0 && query.trim() && <p className='wa-empty'>Geen wijken voor “{query}”.</p>}
                {!loading && options.length > 0 && filtered.length === 0 && !query.trim() && <p className='wa-empty'>Alle wijken geselecteerd.</p>}
            </div>
        </div>
    )
}

export default WijkSelect
